import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Loader2, CheckCircle2, AlertCircle, ArrowLeft } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VALID_SOURCES = ["turtopia", "main", "all"];

export default function DescargarLeads() {
  const { source = "all" } = useParams();
  const [state, setState] = useState("loading"); // loading | done | error
  const [error, setError] = useState("");
  const [count, setCount] = useState(0);

  useEffect(() => {
    const run = async () => {
      if (!VALID_SOURCES.includes(source)) {
        setError(`Fuente no válida: ${source}`);
        setState("error");
        return;
      }
      try {
        const url =
          source === "all"
            ? `${API}/leads/export.csv`
            : `${API}/leads/export.csv?source=${source}`;
        const res = await axios.get(url, { responseType: "blob" });
        const blob = new Blob([res.data], {
          type: "text/csv;charset=utf-8;",
        });
        // Try to count rows (rough)
        try {
          const text = await blob.text();
          const lines = text.split("\n").filter((l) => l.trim().length > 0);
          setCount(Math.max(lines.length - 1, 0));
        } catch {
          /* ignore */
        }
        const objectUrl = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = objectUrl;
        const filename = `tony-yarto-leads-${source}-${new Date()
          .toISOString()
          .slice(0, 10)}.csv`;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
        setState("done");
      } catch (err) {
        setError(err?.message || "Error desconocido");
        setState("error");
      }
    };
    run();
  }, [source]);

  const label =
    source === "turtopia"
      ? "Turtopía · White paper"
      : source === "main"
        ? "Sitio principal"
        : "Todos los leads";

  return (
    <div className="min-h-screen flex items-center justify-center bg-black text-white p-6">
      <div className="w-full max-w-md bg-[#0a0a0a] border border-white/10 p-8 text-center">
        <div className="text-[10px] uppercase tracking-[0.3em] text-accent mb-3">
          Exportar leads
        </div>
        <h1 className="font-display text-2xl md:text-3xl font-black uppercase tracking-tighter mb-2">
          {label}
        </h1>

        {state === "loading" && (
          <div
            className="mt-8 flex flex-col items-center gap-3"
            data-testid="descarga-loading"
          >
            <Loader2 className="animate-spin text-accent" size={28} />
            <p className="text-sm text-white/60">Generando CSV…</p>
          </div>
        )}

        {state === "done" && (
          <div
            className="mt-8 flex flex-col items-center gap-3"
            data-testid="descarga-done"
          >
            <CheckCircle2 className="text-accent" size={32} />
            <p className="text-sm text-white/85">
              Descarga iniciada
              {count > 0 && (
                <span className="block text-white/55 text-xs mt-1">
                  {count} {count === 1 ? "lead" : "leads"} exportados
                </span>
              )}
            </p>
            <p className="text-[11px] text-white/40 mt-2">
              Si no comenzó automáticamente, vuelve a entrar a este enlace.
            </p>
          </div>
        )}

        {state === "error" && (
          <div
            className="mt-8 flex flex-col items-center gap-3"
            data-testid="descarga-error"
          >
            <AlertCircle className="text-red-400" size={28} />
            <p className="text-sm text-white/75">{error}</p>
          </div>
        )}

        <Link
          to="/"
          className="mt-10 inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-white/45 hover:text-white"
        >
          <ArrowLeft size={12} /> Volver al sitio
        </Link>
      </div>
    </div>
  );
}
